import { useMemo } from "react";
import { Pencil, Trash2 } from "lucide-react";

import { formatDateTime } from "@/utils/dateFormatter";
import { formatNameWithTooltip } from "@/utils/nameFormatter";
import { formatUser } from "@/utils/userFormatter";
import { getUnitLabel } from "@/utils/unitFormatter";
import { convertValue } from "@/utils/unitConverter";
import { formatNumber, truncateToDecimals } from "@/utils/numberFormat";

import { QUANTITY_DECIMALS } from "@/components/global.constants";
import useFilterStore from "@/stores/useFilterStore";

import CustomTooltip from "@/components/desktop/tooltip/CustomTooltip";
import CustomBadge from "@/components/desktop/badge/CustomBadge";
import CustomButton from "@/components/desktop/button/CustomButton";
import CustomProductName from "@/components/desktop/products/product_name/CustomProductName";

import { getMovementTypeLabel, getMovementTypeVariant } from "@/constants/movementTypes";

const CHECKBOX_STYLE = { width: "16px", height: "16px", cursor: "pointer", accentColor: "#82bc44" };

export function useInUseHistoryColumns(tableState, selectionState = {}, { onEdit, onDelete } = {}) {
	const { selectedRows = {}, onRowSelect, onSelectAll, allRowsCount = 0 } = selectionState;
	const { filter } = useFilterStore();
	const unitMeasurement = filter?.unit_measurement || "kg_l";
	const canEdit = Boolean(onEdit);
	const canDelete = Boolean(onDelete);

	return useMemo(() => {
		const selectedCount = Object.keys(selectedRows).length;
		const allSelected = allRowsCount > 0 && selectedCount === allRowsCount;

		const selectionColumn = canEdit
			? [
					{
						id: "select",
						Header: () => (
							<input
								type="checkbox"
								style={CHECKBOX_STYLE}
								checked={allSelected}
								onChange={(e) => onSelectAll && onSelectAll(e.target.checked)}
							/>
						),
						Cell: ({ row }) => (
							<input
								type="checkbox"
								style={CHECKBOX_STYLE}
								checked={Boolean(selectedRows[String(row.index)])}
								onChange={(e) => onRowSelect && onRowSelect(String(row.index), row.original, e.target.checked)}
							/>
						),
						disableSortBy: true,
						width: "3%",
					},
				]
			: [];

		const actionColumn =
			canEdit || canDelete
				? [
						{
							id: "actions",
							Header: "Acciones",
							disableSortBy: true,
							width: "8%",
							meta: { cellClassName: "tdCenter" },
							Cell: ({ row }) => {
								const applicationId = row.original.io_ids?.[0];
								if (!applicationId) return null;

								return (
									<div style={{ display: "flex", gap: "8px", justifyContent: "center" }}>
										{canEdit && (
											<CustomButton
												variant="primary"
												size="sm"
												onClick={() => onEdit(applicationId)}
												startIcon={<Pencil size={16} />}
												title="Editar aplicación"
												style={{ padding: "0.5rem" }}
											/>
										)}
										{canDelete && (
											<CustomButton
												variant="danger"
												size="sm"
												onClick={() => onDelete(applicationId)}
												startIcon={<Trash2 size={16} />}
												title="Eliminar aplicación"
												style={{ padding: "0.5rem" }}
											/>
										)}
									</div>
								);
							},
						},
					]
				: [];

		return [
			...selectionColumn,
			{
				Header: "Fecha Salida",
				accessor: "created_date_full",
				width: "7%",
				Cell: ({ value }) => {
					const formattedDateTimeValue = formatDateTime(value);
					const [formattedDate, formattedTime] = formattedDateTimeValue ? formattedDateTimeValue.split(" ") : ["", ""];
					return (
						<div style={{ display: "flex", flexDirection: "column", lineHeight: 1.3 }}>
							<span style={{ whiteSpace: "nowrap" }}>{formattedDate}</span>
							{formattedTime && <span style={{ whiteSpace: "nowrap", fontSize: "0.85em", opacity: 0.6 }}>{formattedTime}</span>}
						</div>
					);
				},
			},
			{
				Header: "Código",
				accessor: "code",
				width: "6%",
				Cell: ({ value }) => {
					if (!value) return <span>-</span>;
					return <span style={{ fontWeight: 700 }}>{value}</span>;
				},
			},
			{
				Header: "Producto",
				accessor: "product_name",
				width: "10%",
				Cell: ({ value, row }) => {
					if (!value) return <span>-</span>;
					return (
						<CustomProductName
							product={{
								code: row.original.product_code,
								value: row.original.product_id,
								label: value,
							}}
						/>
					);
				},
			},
			{
				Header: "Cant. Salida",
				accessor: "output_weight_um",
				width: "6%",
				meta: { cellClassName: "tdRight" },
				Cell: ({ value, row }) => {
					if (row.original.unit_measurement === "u") {
						return formatNumber(value, 0);
					}
					const convertedValue = convertValue(value, row.original.unit_measurement, unitMeasurement);
					return (
						<div style={{ fontWeight: 500 }}>
							{formatNumber(truncateToDecimals(convertedValue, QUANTITY_DECIMALS), QUANTITY_DECIMALS) +
								" " +
								getUnitLabel(row.original.unit_measurement, unitMeasurement)}
						</div>
					);
				},
			},
			{
				Header: "Origen",
				accessor: "estate_parent_origin_name",
				width: "8%",
				Cell: ({ value, row }) => {
					const estateOriginName = row.original.estate_origin_name || "";
					const estateParentOriginName = value || "";

					if (estateOriginName === estateParentOriginName) {
						const { abbreviated, full } = formatNameWithTooltip(estateOriginName);
						return <CustomTooltip text={abbreviated} description={full} />;
					}

					const displayText =
						estateParentOriginName && estateOriginName
							? `${estateParentOriginName} - ${estateOriginName}`
							: estateParentOriginName || estateOriginName;
					const { abbreviated, full } = formatNameWithTooltip(displayText);
					return <CustomTooltip text={abbreviated} description={full} />;
				},
			},
			{
				Header: "Destino",
				accessor: "estate_parent_name",
				width: "8%",
				Cell: ({ value, row }) => {
					const estateName = row.original.estate_name || "";
					const parentName = value || "";

					if (estateName === parentName) {
						const { abbreviated, full } = formatNameWithTooltip(estateName);
						return <CustomTooltip text={abbreviated} description={full} />;
					}

					const displayText = parentName && estateName ? `${parentName} - ${estateName}` : parentName || estateName;
					const { abbreviated, full } = formatNameWithTooltip(displayText);
					return <CustomTooltip text={abbreviated} description={full} />;
				},
			},
			{
				Header: "Centro Costo",
				accessor: "business_center_name",
				width: "18%",
				Cell: ({ value }) => {
					if (!value) return <span>-</span>;

					const businessCenterNames = value.split(",").map((businessCenterName) => businessCenterName.trim());

					return (
						<div style={{ display: "flex", gap: "4px", flexWrap: "wrap" }}>
							{businessCenterNames.map((businessCenterName, index) => (
								<CustomBadge key={index} variant="neutral" size="sm">
									{businessCenterName}
								</CustomBadge>
							))}
						</div>
					);
				},
			},
			{
				Header: "Operador",
				accessor: "operator_name",
				width: "6%",
				Cell: ({ value }) => {
					if (!value) return <span>-</span>;

					const userNames = value.split(",").map((userName) => userName.trim());

					return (
						<div style={{ display: "flex", gap: "4px", flexWrap: "wrap" }}>
							{userNames.map((userName, index) => {
								const { abbreviated } = formatUser(userName);
								return (
									<CustomBadge key={index} variant="neutral" size="sm">
										{abbreviated}
									</CustomBadge>
								);
							})}
						</div>
					);
				},
			},
			{
				Header: "Usuario",
				accessor: "user_name",
				width: "6%",
				Cell: ({ value }) => {
					const { abbreviated, full } = formatUser(value);
					return <CustomTooltip text={abbreviated} description={full} />;
				},
			},
			{
				Header: "N° OA",
				accessor: "application_order_code",
				width: "7%",
				Cell: ({ value }) => {
					if (!value) return <span>-</span>;

					const applicationOrderCodes = value.split(",").map((applicationOrderCode) => applicationOrderCode.trim());

					return (
						<div style={{ display: "flex", gap: "4px", flexWrap: "wrap" }}>
							{applicationOrderCodes.map((applicationOrderCode, index) => (
								<CustomBadge key={index} variant="neutral" size="sm">
									{applicationOrderCode}
								</CustomBadge>
							))}
						</div>
					);
				},
			},
			{
				Header: "Tipo",
				accessor: "type_movement",
				width: "7%",
				meta: { cellClassName: "tdCenter" },
				Cell: ({ value }) => {
					const movementTypeLabel = getMovementTypeLabel(value);
					const movementTypeVariant = getMovementTypeVariant(value);

					return (
						<CustomBadge variant={movementTypeVariant} size="sm">
							{movementTypeLabel}
						</CustomBadge>
					);
				},
			},
			...actionColumn,
		];
	}, [selectedRows, onRowSelect, onSelectAll, allRowsCount, unitMeasurement, canEdit, canDelete, onEdit, onDelete]);
}
